<script lang="ts">
  import { tick, untrack } from "svelte";
  import { backOut, cubicIn } from "svelte/easing";
  import { fly, fade } from "svelte/transition";
  import { SECS_PER_PAGE, sceneGradient, type JumpMarker } from "@piano/shared";
  import { api } from "../lib/api/index.js";
  import { isBoolean, isNumber, readPref, writePref } from "../lib/persisted.js";
  import { router } from "../lib/router.svelte.js";
  import { library } from "../lib/stores/library.svelte.js";
  import { hue } from "../lib/stores/hue.svelte.js";
  import { live } from "../lib/stores/live.svelte.js";
  import { playlists } from "../lib/stores/playlists.svelte.js";
  import { session } from "../lib/stores/session.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import { AutoScroller } from "../lib/viewer/autoscroll.js";
  import { INK_COLORS, INK_SIZES, type Tool } from "../lib/viewer/ink.js";
  import { InkStore } from "../lib/viewer/ink-store.svelte.js";
  import { loadPdf, type LoadedPdf } from "../lib/viewer/pdf.js";
  import {
    clampSpreadStart,
    fitSpread,
    spreadPages,
    spreadStartFor,
    spreadWindow,
    turnSpread,
  } from "../lib/viewer/spread.js";
  import {
    enterFullscreen,
    exitFullscreen,
    fullscreenSupported,
    isFullscreen,
    isStandalone,
    onFullscreenChange,
  } from "../lib/viewer/fullscreen.js";
  import { WakeLock } from "../lib/viewer/wake-lock.js";
  import {
    anchoredScroll,
    captureAnchor,
    clampZoom,
    distance,
    midpoint,
    pinchZoom,
    SPREAD_ZOOM_MIN,
    stepZoom,
    ZOOM_MAX,
    ZOOM_MIN,
    type Box,
    type ZoomAnchor,
  } from "../lib/viewer/zoom.js";
  import Icon from "../components/Icon.svelte";
  import LightPanel from "../components/hue/LightPanel.svelte";
  import PdfPage from "../components/viewer/PdfPage.svelte";

  let { documentId }: { documentId: string } = $props();

  const userId = session.user?.id ?? "";

  // --- Laden -------------------------------------------------------------------
  let loaded = $state<LoadedPdf | null>(null);
  let aspects = $state<number[]>([]);
  let progress = $state(0);
  let failed = $state("");
  let markers = $state<JumpMarker[]>([]);
  let secs = $state<number>(SECS_PER_PAGE.default);
  // svelte-ignore state_referenced_locally
  const ink = new InkStore(documentId);
  const wake = new WakeLock();

  const piece = $derived(library.byId.get(documentId));

  $effect(() => {
    let alive = true;
    void (async () => {
      try {
        const [pdf, prefs] = await Promise.all([
          loadPdf(documentId, (f) => (progress = f)),
          api.prefs(documentId),
        ]);
        if (!alive) return void pdf.destroy();
        const sizes: number[] = [];
        for (let n = 1; n <= pdf.pdf.numPages; n++) {
          const vp = (await pdf.pdf.getPage(n)).getViewport({ scale: 1 });
          sizes.push(vp.height / vp.width);
        }
        if (!alive) return void pdf.destroy();
        aspects = sizes;
        loaded = pdf;
        markers = prefs.jumpMarkers;
        if (prefs.secsPerPage) secs = prefs.secsPerPage;
      } catch (error) {
        failed = error instanceof Error ? error.message : "Die Noten konnten nicht geladen werden";
      }
    })();
    ink.applyPreset(live.settings.annotations.preset);
    void ink.load().catch(() => undefined);
    void api.markOpened(documentId).catch(() => undefined);
    void wake.enable();
    const off = live.onDocumentPrefs((id) => {
      if (id !== documentId) return;
      void ink.refreshIfClean();
      void api.prefs(documentId).then((p) => {
        markers = p.jumpMarkers;
        if (p.secsPerPage) secs = p.secsPerPage;
      });
    });
    const onHide = () => document.visibilityState === "hidden" && ink.flush();
    document.addEventListener("visibilitychange", onHide);
    return () => {
      alive = false;
      off();
      document.removeEventListener("visibilitychange", onHide);
      ink.dispose();
      wake.dispose();
      scroller_?.stop();
      void loaded?.destroy();
    };
  });

  // --- Setlist & Live-Session ---------------------------------------------------
  const setlistId = router.query.get("setlist");
  const playlist = $derived(setlistId ? (playlists.byId.get(setlistId) ?? null) : null);
  const position = $derived.by(() => {
    if (!playlist) return -1;
    const i = Number(router.query.get("i"));
    return playlist.items[i] === documentId ? i : playlist.items.indexOf(documentId);
  });
  const nextId = $derived(playlist && position >= 0 ? (playlist.items[position + 1] ?? null) : null);
  const prevId = $derived(playlist && position > 0 ? (playlist.items[position - 1] ?? null) : null);
  const upNext = $derived(nextId ? library.byId.get(nextId) : undefined);

  function openAt(index: number): void {
    if (!playlist) return;
    const id = playlist.items[index];
    if (!id) return;
    void playlists.setCurrent(playlist.id, index);
    router.go(`/noten/${id}?setlist=${playlist.id}&i=${index}`, { replace: true });
  }

  // Aktuelles Stück am Server festhalten; Fernsteuerung (Handy) folgen.
  let lastCurrentDoc: string | null = null;
  $effect(() => {
    if (!playlist || position < 0) return;
    const currentDoc = playlist.items[playlist.currentIndex] ?? null;
    if (lastCurrentDoc === null) {
      lastCurrentDoc = documentId;
      if (currentDoc !== documentId) void playlists.setCurrent(playlist.id, position);
      return;
    }
    if (currentDoc && currentDoc !== lastCurrentDoc && currentDoc !== documentId) {
      lastCurrentDoc = currentDoc;
      router.go(`/noten/${currentDoc}?setlist=${playlist.id}&i=${playlist.currentIndex}`, { replace: true });
    }
  });

  const device =
    /iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1
      ? "iPad"
      : /iPhone/.test(navigator.userAgent)
        ? "iPhone"
        : "Browser";
  $effect(() => {
    const send = () =>
      api
        .putPresence({
          device,
          documentId,
          playlistId: playlist?.id ?? null,
          index: position >= 0 ? position : null,
        })
        .catch(() => undefined);
    void send();
    const timer = setInterval(send, 45_000);
    return () => clearInterval(timer);
  });
  $effect(() => () => void api.clearPresence(true).catch(() => undefined));

  // --- Layout & Zoom ------------------------------------------------------------
  let scroller: HTMLDivElement | undefined = $state();
  let viewportW = $state(window.innerWidth);
  let zoom = $state(readPref(userId, "viewer.zoom", 1, isNumber));
  const pageWidth = $derived(Math.round(Math.min(viewportW - (ui.wide ? 48 : 16), 1400) * zoom));

  // Doppelseite: nur im Querformat und ab zwei Seiten. Kurze Hochformat-Zwischenstände
  // (Drehen, Safari-Leisten) werfen nicht sofort aus dem Modus.
  let landscape = $state(ui.landscape);
  $effect(() => {
    if (ui.landscape) return void (landscape = true);
    const timer = setTimeout(() => (landscape = false), 300);
    return () => clearTimeout(timer);
  });
  let spreadPref = $state(readPref(userId, "viewer.spread", false, isBoolean));
  let spreadZoom = $state(readPref(userId, "viewer.zoom.spread", 1, isNumber));
  let spreadStart = $state(1);
  let animateTurn = $state(false);
  let turnTimer: ReturnType<typeof setTimeout> | undefined;
  let frameW = $state(0);
  let frameH = $state(0);
  const spreadAvailable = $derived(landscape && aspects.length > 1);
  const spread = $derived(spreadPref && spreadAvailable);
  const spreadList = $derived(spread ? spreadPages(spreadStart, aspects.length) : []);
  const spreadSize = $derived(
    fitSpread(
      spreadList.map((n) => aspects[n - 1] ?? 1.414),
      frameW,
      frameH,
    ),
  );

  // Filmstreifen: alle Seiten auf gemeinsamer Höhe nebeneinander; das „Buch“ zeigt davon
  // genau die aktuelle Doppelseite. Blättern verschiebt den Streifen um eine Seitenbreite.
  const leafWidths = $derived(
    aspects.map((a) => Math.floor((spreadSize.height / (a > 0 ? a : 1.414)) * spreadZoom)),
  );
  const leafX = $derived.by(() => {
    const xs: number[] = [];
    let x = 0;
    for (const w of leafWidths) {
      xs.push(x);
      x += w;
    }
    return xs;
  });
  const bookWidth = $derived(spreadList.reduce((sum, n) => sum + (leafWidths[n - 1] ?? 0), 0));
  const bookHeight = $derived(
    Math.max(0, ...spreadList.map((n) => Math.round((leafWidths[n - 1] ?? 0) * (aspects[n - 1] ?? 1.414)))),
  );
  const stripX = $derived(leafX[(spreadList[0] ?? 1) - 1] ?? 0);
  // Vorab gerendert: Seiten um die Doppelseite herum und an allen Sprungzielen
  const jumpTargets = $derived(markers.flatMap((m) => (m.target ? [m.target.pageNumber] : [])));
  const preload = $derived(spread ? spreadWindow(spreadStart, aspects.length, jumpTargets) : []);

  /** Doppelseite wechseln; nur Nachbarseiten gleiten, Sprünge wechseln sofort. */
  function showSpread(start: number): void {
    animateTurn = Math.abs(start - spreadStart) === 1;
    spreadStart = start;
    clearTimeout(turnTimer);
    turnTimer = setTimeout(() => (animateTurn = false), 450);
  }

  const currentZoom = $derived(spread ? spreadZoom : zoom);
  const zoomMin = $derived(spread ? SPREAD_ZOOM_MIN : ZOOM_MIN);

  /** Lage der sichtbaren Seiten in Koordinaten des Scrollbereichs. */
  function pageBoxes(): Box[] {
    if (!scroller) return [];
    const origin = scroller.getBoundingClientRect();
    const boxes: Box[] = [];
    for (const el of scroller.querySelectorAll<HTMLElement>("[data-page]")) {
      const page = Number(el.dataset.page);
      if (spread && !spreadList.includes(page)) continue; // vorab gerendert, nicht im Bild
      const rect = el.getBoundingClientRect();
      boxes.push({
        page,
        left: rect.left - origin.left + scroller.scrollLeft,
        top: rect.top - origin.top + scroller.scrollTop,
        width: rect.width,
        height: rect.height,
      });
    }
    return boxes;
  }

  /** Die am meisten sichtbare Seite. */
  function currentPage(): number {
    if (!scroller) return 1;
    const top = scroller.scrollTop;
    const bottom = top + scroller.clientHeight;
    let best = { page: 1, visible: -Infinity };
    for (const box of pageBoxes()) {
      const visible = Math.min(bottom, box.top + box.height) - Math.max(top, box.top);
      if (visible > best.visible) best = { page: box.page, visible };
    }
    return best.page;
  }

  function scrollToPage(pageNumber: number): void {
    const el = scroller?.querySelector<HTMLElement>(`[data-page="${pageNumber}"]`);
    const first = scroller?.querySelector<HTMLElement>(`[data-page]`);
    if (!scroller || !el || !first) return;
    scroller.scrollTop = Math.max(0, el.offsetTop - first.offsetTop);
  }

  // Beim Umschalten an derselben Stelle der Noten bleiben.
  let wasSpread = false;
  $effect.pre(() => {
    const active = spread;
    if (active === wasSpread) return;
    wasSpread = active;
    untrack(() => {
      if (active) {
        animateTurn = false;
        spreadStart = clampSpreadStart(currentPage(), aspects.length);
        stopScroll();
      } else {
        const page = spreadStart;
        void tick().then(() => scrollToPage(page));
      }
    });
  });

  function toggleSpread(): void {
    spreadPref = !spreadPref;
    writePref(userId, "viewer.spread", spreadPref);
    more = light = false;
  }

  function anchorAt(screenX: number, screenY: number): ZoomAnchor | null {
    if (!scroller) return null;
    return captureAnchor(
      pageBoxes(),
      scroller.scrollLeft + screenX,
      scroller.scrollTop + screenY,
      screenX,
      screenY,
    );
  }

  function centerAnchor(): ZoomAnchor | null {
    return scroller ? anchorAt(scroller.clientWidth / 2, scroller.clientHeight / 2) : null;
  }

  /** Zoomt und hält dabei den Punkt unter `anchor` (Standard: Bildschirmmitte) fest. */
  function setZoom(value: number, anchor = centerAnchor()): void {
    const next = clampZoom(value, zoomMin, ZOOM_MAX);
    if (next === currentZoom) return;
    if (spread) {
      spreadZoom = next;
      writePref(userId, "viewer.zoom.spread", next);
    } else {
      zoom = next;
      writePref(userId, "viewer.zoom", next);
    }
    void tick().then(() => {
      const box = anchor && pageBoxes().find((b) => b.page === anchor.page);
      if (!scroller || !anchor || !box) return;
      const { left, top } = anchoredScroll(anchor, box);
      scroller.scrollLeft = left;
      scroller.scrollTop = top;
    });
  }

  // Pinch-Zoom mit zwei Fingern: während der Geste nur skalieren (kein Neu-Rendern),
  // beim Loslassen den Zoom übernehmen und die Seiten scharf neu zeichnen.
  let pinch: { startDistance: number; startZoom: number; zoom: number; anchor: ZoomAnchor | null } | null =
    null;
  let pinchShown = $state<number | null>(null);
  let pinchEndedAt = 0;

  const content = () => scroller?.querySelector<HTMLElement>(".pages, .spread") ?? null;

  function twoFingers(event: TouchEvent): [Touch, Touch] | null {
    if (event.touches.length !== 2) return null;
    const a = event.touches[0]!;
    const b = event.touches[1]!;
    const stylus = (t: Touch) => (t as Touch & { touchType?: string }).touchType === "stylus";
    return stylus(a) || stylus(b) ? null : [a, b];
  }

  function pinchStart(event: TouchEvent): void {
    // Im Modus „Stift + Finger“ zeichnen die Finger – dort gibt es kein Pinchen.
    if (mode === "notes" && !penOnly) return;
    const fingers = twoFingers(event);
    const el = content();
    if (!fingers || !scroller || !el) return;
    if (event.cancelable) event.preventDefault();
    const rect = scroller.getBoundingClientRect();
    const mid = midpoint(...fingers);
    const x = mid.x - rect.left;
    const y = mid.y - rect.top;
    pinch = {
      startDistance: distance(...fingers),
      startZoom: currentZoom,
      zoom: currentZoom,
      anchor: anchorAt(x, y),
    };
    el.style.transformOrigin = `${scroller.scrollLeft + x}px ${scroller.scrollTop + y}px`;
  }

  function pinchMove(event: TouchEvent): void {
    if (!pinch) return;
    const fingers = twoFingers(event);
    const el = content();
    if (!fingers || !el) return;
    if (event.cancelable) event.preventDefault();
    pinch.zoom = pinchZoom(pinch.startZoom, pinch.startDistance, distance(...fingers), zoomMin, ZOOM_MAX);
    pinchShown = pinch.zoom;
    el.style.transform = `scale(${pinch.zoom / pinch.startZoom})`;
  }

  function pinchEnd(event: TouchEvent): void {
    if (!pinch || event.touches.length >= 2) return;
    const { zoom: target, anchor } = pinch;
    pinch = null;
    pinchShown = null;
    pinchEndedAt = performance.now();
    const el = content();
    if (el) el.style.transform = el.style.transformOrigin = "";
    setZoom(target, anchor);
  }

  $effect(() => {
    const el = scroller;
    if (!el) return;
    const active = { passive: false } as const;
    const noPageZoom = (event: Event) => event.preventDefault();
    el.addEventListener("touchstart", pinchStart, active);
    el.addEventListener("touchmove", pinchMove, active);
    el.addEventListener("touchend", pinchEnd);
    el.addEventListener("touchcancel", pinchEnd);
    // Safari: eigenes Zoomen der ganzen Seite unterbinden
    el.addEventListener("gesturestart", noPageZoom, active);
    el.addEventListener("wheel", wheel, active);
    return () => {
      el.removeEventListener("wheel", wheel);
      el.removeEventListener("touchstart", pinchStart);
      el.removeEventListener("touchmove", pinchMove);
      el.removeEventListener("touchend", pinchEnd);
      el.removeEventListener("touchcancel", pinchEnd);
      el.removeEventListener("gesturestart", noPageZoom);
    };
  });

  // --- Blättern, Tippen, Tastatur ------------------------------------------------
  let chrome = $state(true);
  let flash = $state<"back" | "fwd" | null>(null);
  let flashSeq = $state(0);
  let flashTimer: ReturnType<typeof setTimeout> | undefined;
  const FLASH_MS = 560;

  function page(direction: 1 | -1): void {
    if (!scroller) return;
    if (spread) {
      const next = turnSpread(spreadStart, direction, aspects.length);
      if (next === null) {
        if (direction === 1 && nextId && mode === "none") openAt(position + 1);
        return;
      }
      showSpread(next);
      // Beim Spielen nach dem Umblättern freie Sicht auf beide Seiten
      if (mode === "none" && !more && !light) chrome = false;
    } else {
      const atEnd = scroller.scrollTop >= scroller.scrollHeight - scroller.clientHeight - 2;
      if (direction === 1 && atEnd && nextId && mode === "none") return openAt(position + 1);
      scroller.scrollBy({ top: direction * scroller.clientHeight * 0.75, behavior: "smooth" });
    }
    flash = direction === 1 ? "fwd" : "back";
    flashSeq += 1;
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => (flash = null), FLASH_MS);
  }

  // Mausrad/Trackpad in der Doppelseite: blättern, sobald es nichts mehr zu scrollen gibt.
  // Pro Wisch-Geste (bis zu einer kurzen Pause) wird höchstens einmal geblättert.
  let wheelSum = 0;
  let wheelLast = 0;
  let wheelTurned = false;
  function wheel(event: WheelEvent): void {
    if (!spread || !scroller || pinch || event.ctrlKey) return;
    const dy = event.deltaMode === 1 ? event.deltaY * 32 : event.deltaY;
    const room =
      dy > 0
        ? scroller.scrollTop < scroller.scrollHeight - scroller.clientHeight - 1
        : scroller.scrollTop > 0;
    if (room || !dy) return;
    event.preventDefault();
    const now = performance.now();
    const gap = now - wheelLast;
    wheelLast = now;
    if (gap > 250) {
      wheelTurned = false;
      wheelSum = 0;
    }
    if (wheelTurned) return;
    wheelSum += dy;
    if (Math.abs(wheelSum) < 40) return;
    wheelTurned = true;
    page(wheelSum > 0 ? 1 : -1);
  }

  function tap(event: MouseEvent): void {
    if (mode !== "none" || placing) return;
    if (performance.now() - pinchEndedAt < 400) return;
    void wake.enable();
    if (more || light) return void (more = light = false);
    const x = event.clientX / window.innerWidth;
    if (x < 0.28) page(-1);
    else if (x > 0.72) page(1);
    else chrome = !chrome;
  }

  function key(event: KeyboardEvent): void {
    if ((event.target as HTMLElement).closest("input, textarea")) return;
    if (ui.confirmRequest || ui.promptRequest) return;
    if (["ArrowRight", "ArrowDown", "PageDown", " "].includes(event.key)) {
      event.preventDefault();
      page(1);
    } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(event.key)) {
      event.preventDefault();
      page(-1);
    } else if (event.key === "Escape") {
      if (light || more) more = light = false;
      else if (mode === "notes") closeNotes();
      else if (mode === "markers") {
        mode = "none";
        placing = null;
      } else close();
    }
  }

  function close(): void {
    ink.flush();
    void exitFullscreen();
    router.back(playlist ? `/setlists/${playlist.id}` : "/");
  }

  // --- Modi: Notizen, Sprünge, Mehr ---------------------------------------------
  let mode = $state<"none" | "notes" | "markers">("none");
  let more = $state(false);
  let light = $state(false);
  let lightButton = $state<HTMLButtonElement>();
  let inkMore = $state(false);
  let inkPages = $state<number[]>([]);
  const penOnly = $derived(live.settings.annotations.inputMode === "pen-only");

  function pickTool(tool: Tool): void {
    ink.tool = tool;
    savePreset();
  }
  function pickColor(color: string): void {
    ink.color = color;
    if (ink.tool === "eraser") ink.tool = "pen";
    savePreset();
  }
  function pickSize(level: number): void {
    ink.size = level;
    savePreset();
  }
  /** Punktgröße (px) für die Vorschau einer Stärkestufe */
  const dot = (level: number) => Math.round(5 + level * 2.4);

  function toggleInkMore(): void {
    inkMore = !inkMore;
    if (inkMore) inkPages = spread ? spreadList : [currentPage()];
  }
  function clearPage(pageNumber: number): void {
    ink.clearPage(pageNumber);
    inkMore = false;
    ui.toast(`Notizen auf Seite ${pageNumber} gelöscht`, "info", {
      label: "Rückgängig",
      run: () => ink.undo(),
    });
  }
  function closeNotes(): void {
    mode = "none";
    inkMore = false;
    void ink.save();
  }
  function onScroll(): void {
    if (inkMore && !spread) inkPages = [currentPage()];
  }
  function savePreset(): void {
    void api.patchSettings({ annotations: { preset: ink.preset } }).catch(() => undefined);
  }
  function enterNotes(): void {
    mode = "notes";
    more = light = false;
    stopScroll();
    if (penOnly) ui.toast("Mit dem Apple Pencil schreiben – Finger scrollen weiter");
  }

  // Sprungmarken
  let placing = $state<{ id: string; role: "source" | "target" } | null>(null);
  let highlight = $state<string | null>(null);

  async function saveMarkers(next: JumpMarker[]): Promise<void> {
    markers = next;
    try {
      markers = (await api.patchPrefs(documentId, { jumpMarkers: next })).jumpMarkers;
    } catch (error) {
      ui.error(error);
    }
  }

  async function jumpTo(marker: JumpMarker): Promise<void> {
    const target = marker.target;
    if (!target || !scroller) return;
    if (spread) {
      const start = spreadStartFor(target.pageNumber, spreadStart, aspects.length);
      if (start !== spreadStart) {
        showSpread(start);
        await tick();
      }
    }
    const box = pageBoxes().find((b) => b.page === target.pageNumber);
    if (!box) return;
    const top = box.top + box.height * (target.y / 100) - scroller.clientHeight * 0.25;
    scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    highlight = marker.id;
    setTimeout(() => (highlight = null), 1800);
  }

  function onMarker(marker: JumpMarker, role: "source" | "target"): void {
    if (mode === "markers") {
      placing = { id: marker.id, role };
      return;
    }
    if (role === "source") jumpTo(marker);
  }

  function newJump(): void {
    const id = `jump-${Date.now().toString(36)}`;
    markers = [...markers, { id, label: `Sprung ${markers.length + 1}`, source: null, target: null }];
    placing = { id, role: "source" };
  }

  function place(pageNumber: number, x: number, y: number): void {
    if (!placing) return;
    const { id, role } = placing;
    const next = markers.map((m) => (m.id === id ? { ...m, [role]: { pageNumber, x, y } } : m));
    const marker = next.find((m) => m.id === id);
    placing = role === "source" && marker && !marker.target ? { id, role: "target" } : null;
    void saveMarkers(next);
  }

  function moveMarker(marker: JumpMarker, role: "source" | "target", x: number, y: number): void {
    const point = marker[role];
    if (!point) return;
    void saveMarkers(markers.map((m) => (m.id === marker.id ? { ...m, [role]: { ...point, x, y } } : m)));
  }

  async function renameMarker(marker: JumpMarker): Promise<void> {
    const label = await ui.prompt({
      title: "Sprung umbenennen",
      label: "Name",
      value: marker.label,
      maxLength: 80,
    });
    if (label && label !== marker.label)
      await saveMarkers(markers.map((m) => (m.id === marker.id ? { ...m, label } : m)));
  }

  async function deleteMarker(marker: JumpMarker): Promise<void> {
    const ok = await ui.confirm({
      title: "Sprung löschen?",
      message: `„${marker.label}“ wird entfernt.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (ok) await saveMarkers(markers.filter((m) => m.id !== marker.id));
  }

  // Auto-Scroll
  let scroller_: AutoScroller | null = null;
  let scrolling = $state(false);
  function toggleScroll(): void {
    if (scrolling) return stopScroll();
    if (!scroller || spread) return;
    const avgHeight = () => pageWidth * (aspects.reduce((a, b) => a + b, 0) / Math.max(1, aspects.length));
    scroller_ = new AutoScroller(
      scroller,
      avgHeight,
      () => secs,
      () => (scrolling = false),
    );
    scroller_.start();
    scrolling = true;
    more = light = false;
    setTimeout(() => scrolling && mode === "none" && !more && (chrome = false), 2200);
  }
  function stopScroll(): void {
    scroller_?.stop();
    scrolling = false;
  }
  function setSecs(value: number): void {
    secs = Math.min(SECS_PER_PAGE.max, Math.max(SECS_PER_PAGE.min, value));
    clearTimeout(secsTimer);
    secsTimer = setTimeout(
      () => void api.patchPrefs(documentId, { secsPerPage: secs }).catch(() => undefined),
      800,
    );
  }
  let secsTimer: ReturnType<typeof setTimeout> | undefined;

  // Vollbild: beim Öffnen von Noten automatisch an (abschaltbar, wird pro Nutzer gemerkt).
  let fullscreenOn = $state(isFullscreen());
  let fullscreenWanted = readPref(userId, "viewer.fullscreen", true, isBoolean);
  $effect(() => onFullscreenChange((active) => (fullscreenOn = active)));
  $effect(() => {
    if (!fullscreenWanted || isStandalone() || !fullscreenSupported()) return;
    // Ohne frische Nutzeraktion lehnt der Browser ab → beim ersten Tippen nachholen.
    const retry = () => {
      stopRetry();
      if (fullscreenWanted) void enterFullscreen();
    };
    const stopRetry = () => {
      window.removeEventListener("pointerup", retry, true);
      window.removeEventListener("keydown", retry, true);
    };
    void enterFullscreen().then((ok) => {
      if (ok) return;
      window.addEventListener("pointerup", retry, true);
      window.addEventListener("keydown", retry, true);
    });
    return stopRetry;
  });

  async function fullscreen(): Promise<void> {
    light = false;
    if (fullscreenOn) {
      fullscreenWanted = false;
      writePref(userId, "viewer.fullscreen", false);
      return exitFullscreen();
    }
    fullscreenWanted = true;
    writePref(userId, "viewer.fullscreen", true);
    if (await enterFullscreen()) return;
    ui.toast(
      isStandalone() || !fullscreenSupported()
        ? "Tipp: Über „Zum Home-Bildschirm“ läuft die App im Vollbild"
        : "Vollbild wird hier nicht unterstützt",
    );
  }

  const dockVisible = $derived(chrome || mode !== "none");

  // --- Dock-Motion --------------------------------------------------------------
  // Das Dock verwandelt sich je nach Ansicht (Werkzeuge, Mehr, Notizen, Strichstärke,
  // Sprünge): die Breite federt zum neuen Inhalt, der alte Inhalt verblasst an Ort und
  // Stelle, der neue fächert von links nach rechts auf.
  const dockView = $derived(
    mode === "notes" ? (inkMore ? "ink" : "notes") : mode === "markers" ? "markers" : more ? "more" : "tools",
  );
  let faceWidth = $state<number | null>(null);
  let bottomWidth = $state(0);
  /** Passt die Ansicht nicht ganz hinein (Handy), wird das Dock seitlich scrollbar. */
  const dockScrolls = $derived(faceWidth !== null && faceWidth + 16 > bottomWidth - 72);
  let currentFace: HTMLElement | null = null;
  const reducedMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Das Dock erscheint erst, wenn die Noten stehen – eine halbe Sekunde später.
  let dockReady = $state(false);
  $effect(() => {
    if (!loaded) return;
    const timer = setTimeout(() => (dockReady = true), 500);
    return () => clearTimeout(timer);
  });

  /** Kreis des Docks (Höhe), aus dem es sich entfaltet und in den es sich zurückzieht. */
  const DOCK_SEED = 60;
  /** Federnd mit sanftem Überschwingen (≈ 3 %). */
  const spring = (t: number) => 1 + 2.2 * Math.pow(t - 1, 3) + 1.2 * Math.pow(t - 1, 2);

  /** Dock entfaltet sich von der Mitte gleichzeitig nach links und rechts; die Knöpfe
   *  erscheinen von innen nach außen, sobald der Rand sie erreicht. */
  function unfold(node: HTMLElement) {
    if (reducedMotion()) return { duration: 160, css: (t: number) => `opacity: ${t}` };
    const full = node.offsetWidth;
    const box = node.getBoundingClientRect();
    const mid = box.left + box.width / 2;
    for (const child of node.querySelectorAll<HTMLElement>(".face > *")) {
      const r = child.getBoundingClientRect();
      const reach = Math.abs(r.left + r.width / 2 - mid) / Math.max(1, box.width / 2);
      child.style.animationDelay = `${Math.round(80 + reach * 300)}ms`;
    }
    return {
      duration: 700,
      easing: spring,
      css: (t: number) =>
        `width: ${DOCK_SEED + (full - DOCK_SEED) * t}px; opacity: ${Math.min(1, t * 5)};`,
    };
  }

  /** Gegenstück: zur Mitte zusammenziehen und verblassen. */
  function fold(node: HTMLElement) {
    if (reducedMotion()) return { duration: 120, css: (t: number) => `opacity: ${t}` };
    const full = node.offsetWidth;
    return {
      duration: 320,
      easing: cubicIn,
      css: (t: number) =>
        `width: ${DOCK_SEED + (full - DOCK_SEED) * t}px; opacity: ${Math.min(1, t * 3)};`,
    };
  }

  /** Misst die aktuelle Dock-Ansicht; die Breite des Docks folgt ihr per Transition. */
  function face(node: HTMLElement) {
    currentFace = node;
    const observer = new ResizeObserver(() => {
      if (currentFace === node) faceWidth = node.offsetWidth;
    });
    observer.observe(node);
    return { destroy: () => observer.disconnect() };
  }

  /** Abgelöste Ansicht: aus dem Fluss nehmen und an ihrer Stelle verblassen lassen. */
  function faceOut(node: HTMLElement) {
    const { offsetLeft, offsetTop, offsetWidth } = node;
    Object.assign(node.style, {
      position: "absolute",
      left: `${offsetLeft}px`,
      top: `${offsetTop}px`,
      width: `${offsetWidth}px`,
      margin: "0",
    });
    if (reducedMotion()) return { duration: 100, css: (t: number) => `opacity: ${t}` };
    return {
      duration: 200,
      easing: cubicIn,
      css: (t: number, u: number) =>
        `opacity: ${t}; transform: scale(${0.92 + 0.08 * t}); filter: blur(${u * 6}px);`,
    };
  }

  /** Gleitende Markierung unter dem aktiven Element einer Gruppe (`.on` bzw. aria-checked). */
  function slider(node: HTMLElement, _active: unknown) {
    const mark = node.querySelector<HTMLElement>(":scope > .mark");
    let placed = false;
    const place = () => {
      if (!mark) return;
      const on = node.querySelector<HTMLElement>(":scope > .on, :scope > [aria-checked='true']");
      mark.style.opacity = on ? "1" : "0";
      if (!on) return;
      if (!placed) mark.style.transition = "none";
      mark.style.width = `${on.offsetWidth}px`;
      mark.style.transform = `translateX(${on.offsetLeft}px)`;
      if (!placed) {
        void mark.offsetWidth;
        mark.style.transition = "";
        placed = true;
      }
    };
    const observer = new ResizeObserver(place);
    observer.observe(node);
    place();
    return {
      update: () => void tick().then(place),
      destroy: () => observer.disconnect(),
    };
  }

  // --- Licht (Philips Hue) ------------------------------------------------------
  // Vor dem Spielen immer den echten Zustand (Szene, Raum an/aus, Lampen) von der Bridge holen
  void hue.loadDock({ fresh: true });
  let lightTimer: ReturnType<typeof setTimeout> | undefined;
  /** Szene gewählt: kurz stehen lassen, dann Panel und Leisten weg – „Erledigt, spiel.“ */
  function lightPicked(): void {
    clearTimeout(lightTimer);
    lightTimer = setTimeout(() => {
      light = false;
      if (mode === "none" && !more) chrome = false;
    }, 900);
  }
  $effect(() => () => clearTimeout(lightTimer));

  // Titel oben links: löst sich nach einigen Sekunden nach links auf und kommt mit
  // den Leisten wieder (Tippen in die Mitte).
  const TITLE_VISIBLE_MS = 10_000;
  let titleShown = $state(true);
  $effect(() => {
    if (!dockVisible) return;
    titleShown = true;
    const timer = setTimeout(() => (titleShown = false), TITLE_VISIBLE_MS);
    return () => clearTimeout(timer);
  });

  function dissolve(_node: Element, { duration = 900 }: { duration?: number } = {}) {
    return {
      duration,
      easing: cubicIn,
      css: (t: number, u: number) =>
        `opacity: ${t}; transform: translateX(${-u * 64}px); filter: blur(${u * 8}px);`,
    };
  }
  const title = $derived(piece?.name ?? "Noten");
  const meta = $derived(
    playlist && position >= 0
      ? `${playlist.name} · ${position + 1} von ${playlist.items.length}`
      : spread
        ? `${piece?.artist ? piece.artist + " · " : ""}Seite ${spreadList.join("–")} von ${aspects.length}`
        : `${piece?.artist ? piece.artist + " · " : ""}${aspects.length || piece?.pageCount || ""} Seiten`,
  );
</script>

<svelte:window onkeydown={key} onresize={() => (viewportW = window.innerWidth)} />

{#snippet sheet(pageNumber: number, width: number, eager: boolean, renderDelay = 0)}
  <PdfPage
    pdf={loaded!.pdf}
    {pageNumber}
    {width}
    aspect={aspects[pageNumber - 1] ?? 1.414}
    {ink}
    inkActive={mode === "notes"}
    {penOnly}
    {markers}
    markersEditing={mode === "markers"}
    placing={Boolean(placing)}
    highlightId={highlight}
    {eager}
    {renderDelay}
    onmarker={onMarker}
    onplace={place}
    onmove={moveMarker}
  />
{/snippet}

<div class="viewer" role="dialog" aria-modal="true" aria-label={title} in:fade={{ duration: 220 }}>
  {#if hue.echo}
    {#key hue.echo.at}
      <div
        class="glow"
        aria-hidden="true"
        style:--glow-a={hue.echo.colors[0]}
        style:--glow-b={hue.echo.colors[1] ?? hue.echo.colors[0]}
      ></div>
    {/key}
  {/if}
  <div class="frame" aria-hidden="true" bind:clientWidth={frameW} bind:clientHeight={frameH}></div>
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div class="scroller" bind:this={scroller} onclick={tap} onscroll={onScroll}>
    {#if failed}
      <div class="state">
        <p>{failed}</p>
        <button type="button" class="btn btn-ghost" onclick={close}>Zurück</button>
      </div>
    {:else if !loaded}
      <div class="state">
        <span class="bar"><span style:width="{Math.round(progress * 100)}%"></span></span>
      </div>
    {:else if spread}
      <div class="spread">
        <!-- erst rendern, wenn der Rahmen vermessen ist -->
        {#if spreadSize.height > 0}
          <div
            class="book"
            class:placing={Boolean(placing)}
            style:width="{bookWidth}px"
            style:height="{bookHeight}px"
          >
            <div class="strip" class:animate={animateTurn} style:transform="translateX({-stripX}px)">
              {#each preload as n (n)}
                {@const shown = spreadList.includes(n)}
                <div class="leaf" style:left="{leafX[n - 1] ?? 0}px" aria-hidden={!shown} inert={!shown}>
                  <!-- sichtbare Seiten zuerst, die übrigen kurz danach im Hintergrund -->
                  {@render sheet(n, leafWidths[n - 1] ?? 0, true, shown ? 0 : 150)}
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </div>
    {:else}
      <div class="pages">
        {#each aspects as _, i (i)}
          {@render sheet(i + 1, pageWidth, i === 0)}
        {/each}
        {#if nextId && upNext}
          <button type="button" class="end-next" onclick={(e) => (e.stopPropagation(), openAt(position + 1))}>
            <span class="t3">Weiter mit</span>
            <strong>{upNext.name}</strong>
            <Icon name="next" />
          </button>
        {/if}
      </div>
    {/if}
  </div>

  {#if flash}
    {#key flashSeq}
      <span class="zone {flash}" style:--flash="{FLASH_MS}ms" aria-hidden="true">
        <span class="flash"><Icon name={flash === "fwd" ? "forward" : "back"} size={36} /></span>
      </span>
    {/key}
  {/if}

  {#if dockVisible}
    <!-- Oben links bleibt im iPad-Vollbild frei für das System-X; der Zurück-Knopf sitzt unten. -->
    <header class="top" class:fs={fullscreenOn} transition:fly={{ y: -20, duration: 350 }}>
      {#if titleShown}
        <div class="glass title" transition:dissolve>
          <strong class="ellipsis">{title}</strong>
          <span class="t3 ellipsis">{meta}</span>
        </div>
      {/if}
      {#if ink.saving === "error"}<span class="glass warn"
          >Notizen nicht gespeichert – erneuter Versuch …</span
        >{/if}
    </header>

    {#if upNext && mode === "none"}
      <button
        type="button"
        class="upnext glass"
        transition:fly={{ y: 30, duration: 400 }}
        onclick={() => openAt(position + 1)}
      >
        <img src={upNext.thumb} alt="" />
        <span><span class="t3 kicker">Als Nächstes</span><strong class="ellipsis">{upNext.name}</strong></span
        >
      </button>
    {/if}

    {#if placing}
      <div class="hint glass" transition:fly={{ y: -10, duration: 300 }}>
        {placing.role === "source"
          ? "Tippe auf die Stelle, an der gesprungen wird (↩)"
          : "Tippe auf das Ziel des Sprungs (★)"}
        <button type="button" class="btn btn-ghost small" onclick={() => (placing = null)}>Abbrechen</button>
      </div>
    {/if}

    {#if light && mode === "none"}
      <LightPanel onpicked={lightPicked} anchor={lightButton} />
    {/if}
  {/if}

  <!-- Zurück sofort; das Dock entfaltet sich erst kurz nach dem Laden der Noten. -->
  <div class="bottom" bind:clientWidth={bottomWidth}>
    {#if dockVisible}
      <button
        type="button"
        class="icon-btn glass close"
        aria-label="Schließen"
        in:fly={{ x: -40, duration: 480, easing: backOut }}
        out:fly={{ x: -40, duration: 260, easing: cubicIn }}
        onclick={close}><Icon name="back" /></button
      >
    {/if}
    {#if dockVisible && dockReady}
      <nav
        class="dock"
        class:editing={mode !== "none"}
        class:scroll={dockScrolls}
        style:width={faceWidth === null ? null : `${faceWidth + 16}px`}
        in:unfold
        out:fold
        aria-label="Werkzeuge"
      >
        {#key dockView}
          <div class="face" use:face out:faceOut>
            {#if dockView === "tools"}
              {#if playlist}
                <button
                  type="button"
                  class="icon-btn"
                  aria-label="Vorheriges Stück"
                  disabled={!prevId}
                  onclick={() => openAt(position - 1)}><Icon name="prev" /></button
                >
                <button
                  type="button"
                  class="icon-btn"
                  aria-label="Nächstes Stück"
                  disabled={!nextId}
                  onclick={() => openAt(position + 1)}><Icon name="next" /></button
                >
                <span class="sep"></span>
              {/if}
              <div class="pill" role="group" aria-label="Zoom">
                <button
                  type="button"
                  class="icon-btn small"
                  aria-label="Verkleinern"
                  disabled={currentZoom <= zoomMin}
                  onclick={() => setZoom(stepZoom(currentZoom, -1, zoomMin))}
                  ><Icon name="minus" size={16} /></button
                >
                <span class="num">{Math.round((pinchShown ?? currentZoom) * 100)} %</span>
                <button
                  type="button"
                  class="icon-btn small"
                  aria-label="Vergrößern"
                  disabled={currentZoom >= ZOOM_MAX}
                  onclick={() => setZoom(stepZoom(currentZoom, 1, zoomMin))}
                  ><Icon name="plus" size={16} /></button
                >
              </div>
              {#if spreadAvailable}
                <button
                  type="button"
                  class="tool"
                  class:on={spread}
                  aria-pressed={spread}
                  onclick={toggleSpread}
                  ><Icon name="dual" size={20} /><span class="lbl">Doppelseite</span></button
                >
              {/if}
              <span class="sep"></span>
              <button type="button" class="tool" onclick={enterNotes}
                ><Icon name="pen" size={20} /><span class="lbl">Notizen</span></button
              >
              <button
                type="button"
                class="tool"
                onclick={() => ((mode = "markers"), (more = light = false), stopScroll())}
                ><Icon name="jump" size={20} /><span class="lbl">Sprünge</span></button
              >
              {#if hue.hasDock}
                <button
                  bind:this={lightButton}
                  type="button"
                  class="tool"
                  class:on={light}
                  aria-expanded={light}
                  aria-label="Licht"
                  onclick={() => (
                    (light = !light),
                    (more = false),
                    light && void hue.loadDock({ fresh: true })
                  )}
                  ><span
                    class="bulb"
                    class:lit={Boolean(hue.activeScene)}
                    style:--ring={hue.activeScene ? sceneGradient(hue.activeScene.colors) : null}
                    style:--ring-glow={hue.activeScene?.colors[0] ?? null}><Icon name="bulb" size={20} /></span
                  ><span class="lbl">Licht</span></button
                >
              {/if}
              <button
                type="button"
                class="tool"
                class:on={scrolling}
                aria-label="Mehr: Auto-Scroll, Vollbild"
                onclick={() => ((more = true), (light = false))}><Icon name="more" size={20} /></button
              >
            {:else if dockView === "more"}
              <button type="button" class="icon-btn" aria-label="Zurück" onclick={() => (more = false)}
                ><Icon name="back" size={20} /></button
              >
              <span class="sep"></span>
              <button
                type="button"
                class="tool"
                class:on={scrolling}
                role="switch"
                aria-checked={scrolling}
                aria-label="Auto-Scroll"
                disabled={spread}
                onclick={toggleScroll}
                ><Icon name="autoscroll" size={20} /><span class="lbl">Auto-Scroll</span></button
              >
              {#if spread}
                <span class="t3 aside">Nur in der Einzelansicht</span>
              {:else}
                <div class="pill" role="group" aria-label="Tempo">
                  <button
                    type="button"
                    class="icon-btn small"
                    aria-label="Langsamer"
                    disabled={secs >= SECS_PER_PAGE.max}
                    onclick={() => setSecs(secs + 5)}><Icon name="minus" size={16} /></button
                  >
                  <span class="num tempo">{secs} s/Seite</span>
                  <button
                    type="button"
                    class="icon-btn small"
                    aria-label="Schneller"
                    disabled={secs <= SECS_PER_PAGE.min}
                    onclick={() => setSecs(secs - 5)}><Icon name="plus" size={16} /></button
                  >
                </div>
              {/if}
              <span class="sep"></span>
              <button
                type="button"
                class="tool"
                class:on={fullscreenOn}
                role="switch"
                aria-checked={fullscreenOn}
                aria-label="Vollbild"
                onclick={fullscreen}><Icon name="fullscreen" size={20} /><span class="lbl">Vollbild</span></button
              >
              <button
                type="button"
                class="tool"
                aria-label="Stück-Aktionen"
                onclick={() => ((more = false), (ui.actionsFor = documentId))}
                ><Icon name="info" size={20} /><span class="lbl">Stück-Aktionen</span></button
              >
            {:else if dockView === "notes"}
              <div class="seg" use:slider={ink.tool}>
                <span class="mark" aria-hidden="true"></span>
                {#each [["pen", "Stift"], ["highlighter", "Marker"], ["eraser", "Radierer"]] as [tool, label] (tool)}
                  <button
                    type="button"
                    class="tool"
                    class:on={ink.tool === tool}
                    aria-label={label}
                    onclick={() => pickTool(tool as Tool)}
                  >
                    <Icon name={tool!} size={20} /><span class="lbl">{label}</span>
                  </button>
                {/each}
              </div>
              <span class="sep"></span>
              <div class="swatches">
                {#each INK_COLORS as color (color.value)}
                  <button
                    type="button"
                    class="swatch"
                    class:on={ink.color === color.value && ink.tool !== "eraser"}
                    style:background={color.value}
                    aria-label="Farbe {color.name}"
                    onclick={() => pickColor(color.value)}
                  ></button>
                {/each}
              </div>
              <button
                type="button"
                class="tool size-btn"
                aria-label="Strichstärke, Notizen löschen"
                onclick={toggleInkMore}
                ><span
                  class="size-dot"
                  style:width="{dot(ink.size)}px"
                  style:height="{dot(ink.size)}px"
                  style:background={ink.tool === "eraser" ? "var(--text-2)" : ink.color}
                ></span></button
              >
              <span class="sep"></span>
              <button
                type="button"
                class="icon-btn"
                aria-label="Rückgängig"
                disabled={!ink.canUndo}
                onclick={() => ink.undo()}><Icon name="undo" size={20} /></button
              >
              <button type="button" class="btn btn-primary done" onclick={closeNotes}>Fertig</button>
            {:else if dockView === "ink"}
              <button type="button" class="icon-btn" aria-label="Zurück" onclick={toggleInkMore}
                ><Icon name="back" size={20} /></button
              >
              <span class="sep"></span>
              <div class="seg sizes" role="radiogroup" aria-label="Strichstärke" use:slider={ink.size}>
                <span class="mark" aria-hidden="true"></span>
                {#each INK_SIZES as level (level)}
                  <button
                    type="button"
                    class="size"
                    role="radio"
                    aria-checked={ink.size === level}
                    aria-label="Stärke {level}"
                    onclick={() => pickSize(level)}
                  >
                    <span
                      style:width="{dot(level)}px"
                      style:height="{dot(level)}px"
                      style:background={ink.tool === "eraser" ? "var(--text-2)" : ink.color}
                      style:opacity={ink.tool === "highlighter" ? 0.6 : 1}
                    ></span>
                  </button>
                {/each}
              </div>
              <span class="sep"></span>
              {#each inkPages as n (n)}
                <button
                  type="button"
                  class="tool danger"
                  aria-label="Notizen auf Seite {n} löschen"
                  disabled={!ink.hasInk(n)}
                  onclick={() => clearPage(n)}
                  ><Icon name="trash" size={20} /><span class="lbl">Seite {n} leeren</span></button
                >
              {/each}
            {:else}
              <span class="dock-title">Sprünge</span>
              {#if spread}
                <button
                  type="button"
                  class="icon-btn small"
                  aria-label="Seite zurück"
                  disabled={turnSpread(spreadStart, -1, aspects.length) === null}
                  onclick={() => page(-1)}><Icon name="back" size={18} /></button
                >
                <span class="num pages-num">S. {spreadList.join("–")}</span>
                <button
                  type="button"
                  class="icon-btn small"
                  aria-label="Seite vor"
                  disabled={turnSpread(spreadStart, 1, aspects.length) === null}
                  onclick={() => page(1)}><Icon name="forward" size={18} /></button
                >
                <span class="sep"></span>
              {/if}
              {#each markers as marker (marker.id)}
                <span class="marker-chip">
                  <button type="button" class="tool on" onclick={() => jumpTo(marker)}>
                    <Icon name="jump" size={18} />{marker.label}{#if marker.source && marker.target}<span
                        class="t3"
                      >
                        · S. {marker.source.pageNumber} → {marker.target.pageNumber}</span
                      >{/if}
                  </button>
                  <button
                    type="button"
                    class="icon-btn small"
                    aria-label="Umbenennen"
                    onclick={() => renameMarker(marker)}><Icon name="pen" size={14} /></button
                  >
                  <button
                    type="button"
                    class="icon-btn small"
                    aria-label="Löschen"
                    onclick={() => deleteMarker(marker)}><Icon name="trash" size={14} /></button
                  >
                </span>
              {/each}
              <button type="button" class="tool" onclick={newJump}
                ><Icon name="plus" size={18} /><span class="lbl">Neuer Sprung</span></button
              >
              <span class="sep"></span>
              <button
                type="button"
                class="btn btn-primary done"
                onclick={() => ((mode = "none"), (placing = null))}>Fertig</button
              >
            {/if}
          </div>
        {/key}
      </nav>
    {/if}
  </div>
</div>
                              

<style>
  .viewer {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: var(--ground);
  }
  .scroller {
    position: absolute;
    inset: 0;
    overflow: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x pan-y;
  }
  .frame {
    position: absolute;
    inset: calc(12px + var(--safe-top)) calc(12px + var(--safe-right)) calc(12px + var(--safe-bottom))
      calc(12px + var(--safe-left));
    visibility: hidden;
    pointer-events: none;
  }
  .spread {
    display: flex;
    align-items: center;
    justify-content: center;
    width: max-content;
    min-width: 100%;
    min-height: 100%;
    padding: calc(12px + var(--safe-top)) calc(12px + var(--safe-right)) calc(12px + var(--safe-bottom))
      calc(12px + var(--safe-left));
  }
  /* Beide Seiten bündig wie ein aufgeschlagenes Heft: ein gemeinsamer Schatten,
     runde Ecken nur außen. */
  /* Beide Seiten bündig wie ein aufgeschlagenes Heft: ein gemeinsamer Schatten,
     runde Ecken nur außen. Was links und rechts im Streifen liegt, ist abgeschnitten. */
  .book {
    position: relative;
    flex: 0 0 auto;
    overflow: hidden;
    border-radius: 4px;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
  }
  .book.placing {
    outline: 3px dashed var(--accent);
    outline-offset: 6px;
  }
  .book :global(.page) {
    box-shadow: none;
    border-radius: 0;
  }
  .book :global(.page.placing) {
    outline: none;
  }
  .strip {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    will-change: transform;
  }
  .strip.animate {
    transition: transform 360ms cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  .leaf {
    position: absolute;
    top: 0;
  }
  @media (prefers-reduced-motion: reduce) {
    .strip.animate {
      transition-duration: 120ms;
    }
  }
  .pages {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding: calc(84px + var(--safe-top)) 8px calc(120px + var(--safe-bottom));
    min-width: min-content;
  }
  .state {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-items: center;
    justify-content: center;
    color: var(--text-2);
  }
  .bar {
    width: 220px;
    height: 4px;
    border-radius: 2px;
    background: var(--raised);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--accent);
    transition: width var(--dur-fast);
  }
  .glass {
    background: rgba(24, 24, 29, 0.88);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  }
  .top {
    position: absolute;
    top: calc(14px + var(--safe-top));
    left: calc(14px + var(--safe-left));
    right: calc(14px + var(--safe-right));
    display: flex;
    align-items: center;
    gap: 12px;
    pointer-events: none;
  }
  .top > * {
    pointer-events: auto;
  }
  /* Vollbild auf dem iPad: oben links liegt das System-X zum Verlassen */
  .top.fs {
    left: calc(72px + var(--safe-left));
  }
  .title {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 6px 14px;
    border-radius: 14px;
    min-width: 0;
    max-width: 60vw;
  }
  .title strong {
    font-size: 14px;
  }
  .title span,
  .warn {
    font-size: 12px;
    font-weight: 600;
  }
  .warn {
    padding: 8px 12px;
    border-radius: 12px;
    color: var(--accent-2);
  }
  .upnext {
    position: absolute;
    right: calc(18px + var(--safe-right));
    bottom: calc(96px + var(--safe-bottom));
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 14px 6px 6px;
    border-radius: 14px;
    text-align: left;
    max-width: 280px;
  }
  .upnext img {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: cover;
    object-position: top;
    background: var(--paper);
  }
  .upnext > span {
    display: flex;
    flex-direction: column;
    min-width: 0;
    font-size: 13px;
  }
  .kicker {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .hint {
    position: absolute;
    top: calc(80px + var(--safe-top));
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 8px 8px 16px;
    border-radius: var(--r-pill);
    font-weight: 700;
    font-size: 14px;
    white-space: nowrap;
  }
  /* Untere Leiste: Zurück links, Dock in der Mitte (weicht bei wenig Platz nach rechts aus) */
  .bottom {
    position: absolute;
    left: calc(14px + var(--safe-left));
    right: calc(14px + var(--safe-right));
    bottom: calc(18px + var(--safe-bottom));
    display: grid;
    grid-template-columns: minmax(52px, 1fr) minmax(0, auto) minmax(0, 1fr);
    align-items: center;
    column-gap: 10px;
    pointer-events: none;
  }
  .bottom > * {
    pointer-events: auto;
  }
  .close {
    grid-column: 1;
    justify-self: start;
    width: 52px;
    height: 52px;
    color: var(--text);
  }
  .dock {
    grid-column: 2;
    position: relative;
    display: flex;
    justify-content: center;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 8px;
    border-radius: var(--r-pill);
    background: rgba(24, 24, 29, 0.92);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    box-shadow:
      0 18px 50px rgba(0, 0, 0, 0.55),
      inset 0 0 0 1px rgba(255, 255, 255, 0.06);
    scrollbar-width: none;
    transition:
      width var(--dur-sheet) var(--ease-spring),
      box-shadow var(--dur-base);
  }
  .dock.scroll {
    justify-content: flex-start;
  }
  .dock::-webkit-scrollbar {
    display: none;
  }
  /* Bearbeiten (Notizen, Sprünge): dezenter Akzentrand */
  .dock.editing {
    box-shadow:
      0 18px 50px rgba(0, 0, 0, 0.55),
      inset 0 0 0 1.5px rgba(77, 141, 255, 0.45),
      0 0 32px rgba(77, 141, 255, 0.16);
  }
  /* Eine Dock-Ansicht: beim Aufziehen mittig aufgedeckt, bei Überbreite scrollbar */
  .face {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  /* Neue Ansicht fächert von links nach rechts auf */
  .face > * {
    animation: face-in var(--dur-sheet) var(--ease-spring) backwards;
  }
  .face > :nth-child(2) {
    animation-delay: 25ms;
  }
  .face > :nth-child(3) {
    animation-delay: 50ms;
  }
  .face > :nth-child(4) {
    animation-delay: 75ms;
  }
  .face > :nth-child(5) {
    animation-delay: 100ms;
  }
  .face > :nth-child(6) {
    animation-delay: 125ms;
  }
  .face > :nth-child(7) {
    animation-delay: 150ms;
  }
  .face > :nth-child(8) {
    animation-delay: 175ms;
  }
  .face > :nth-child(n + 9) {
    animation-delay: 200ms;
  }
  @keyframes face-in {
    from {
      opacity: 0;
      transform: translateY(6px) scale(0.7);
      filter: blur(4px);
    }
  }
  /* Segment mit gleitender Markierung (Werkzeug, Strichstärke) */
  .seg {
    position: relative;
    display: flex;
    align-items: center;
    gap: 2px;
  }
  .seg > :not(.mark) {
    position: relative;
  }
  .mark {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: var(--r-pill);
    background: var(--accent-soft);
    pointer-events: none;
    transition:
      transform var(--dur-sheet) var(--ease-spring),
      width var(--dur-sheet) var(--ease-spring),
      opacity var(--dur-fast);
  }
  .seg .tool.on {
    background: transparent;
  }
  .sep {
    flex: 0 0 1px;
    height: 28px;
    background: rgba(255, 255, 255, 0.12);
    margin: 0 6px;
  }
  .pill {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 44px;
    padding: 0 6px;
    border-radius: var(--r-pill);
    background: rgba(255, 255, 255, 0.06);
  }
  .num {
    min-width: 56px;
    text-align: center;
    font-weight: 700;
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .icon-btn.small {
    width: 34px;
    height: 34px;
  }
  .tool {
    height: 44px;
    min-width: 44px;
    padding: 0 12px;
    border-radius: var(--r-pill);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: var(--text-2);
    font-weight: 700;
    font-size: 13px;
    white-space: nowrap;
    transition:
      background var(--dur-fast),
      color var(--dur-fast),
      transform var(--dur-tap);
  }
  .tool:hover {
    color: var(--text);
  }
  .tool.on {
    background: var(--accent-soft);
    color: var(--accent);
  }
  .tool:active:not(:disabled) {
    transform: scale(0.92);
  }
  .tool:disabled {
    opacity: 0.4;
  }
  .tool.danger {
    color: var(--danger);
  }
  .aside {
    padding: 0 8px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
  }
  .tempo {
    min-width: 76px;
  }
  .bulb {
    position: relative;
    display: inline-flex;
  }
  .tool.on .bulb {
    animation: bulb-on 520ms var(--ease-pop);
  }
  @keyframes bulb-on {
    40% {
      transform: scale(1.22) rotate(-8deg);
    }
  }
  .bulb.lit::after {
    content: "";
    position: absolute;
    right: -4px;
    bottom: -2px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--ring);
    box-shadow:
      0 0 0 2px #18181d,
      0 0 10px var(--ring-glow);
    animation: ring-in var(--dur-stage) var(--ease-pop) both;
  }
  @keyframes ring-in {
    from {
      transform: scale(0);
    }
  }
  /* Licht-Echo: Szenenfarbe blüht hinter den Noten kurz auf und klingt wieder aus. */
  .glow {
    position: absolute;
    inset: -10%;
    pointer-events: none;
    background:
      radial-gradient(ellipse 60% 45% at 35% 60%, var(--glow-a), transparent 70%),
      radial-gradient(ellipse 55% 40% at 70% 40%, var(--glow-b), transparent 70%);
    filter: blur(120px);
    opacity: 0;
    animation: echo 4400ms var(--ease-out) forwards;
  }
  @keyframes echo {
    32% {
      opacity: 0.22;
    }
    to {
      opacity: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .glow {
      display: none;
    }
  }
  .swatches {
    display: flex;
    gap: 12px;
    padding: 0 8px;
  }
  .swatch {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.25);
    transition: transform var(--dur-base) var(--ease-pop);
  }
  .swatch.on {
    transform: scale(1.25);
    box-shadow:
      0 0 0 3px #18181d,
      0 0 0 5px var(--text);
  }
  .done {
    height: 44px;
    padding: 0 18px;
    border-radius: var(--r-pill);
  }
  .dock-title {
    padding: 0 10px 0 14px;
    font-weight: 800;
    font-size: 14px;
  }
  .marker-chip {
    display: inline-flex;
    align-items: center;
  }
  .sizes {
    gap: 4px;
  }
  .size {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition:
      background var(--dur-fast),
      transform var(--dur-tap) var(--ease-out);
  }
  .size span {
    border-radius: 50%;
    transition: transform var(--dur-base) var(--ease-pop);
  }
  .size:hover {
    background: rgba(255, 255, 255, 0.06);
  }
  .size:active {
    transform: scale(0.9);
  }
  .sizes .mark {
    border-radius: 50%;
    box-shadow: inset 0 0 0 2px var(--accent);
  }
  .size-btn {
    width: 44px;
    padding: 0;
  }
  .size-dot {
    border-radius: 50%;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.12);
    transition:
      width var(--dur-base) var(--ease-pop),
      height var(--dur-base) var(--ease-pop);
  }
  .grow {
    flex: 1;
  }
  /* Rückmeldung beim Blättern: die getippte Randfläche leuchtet kurz auf */
  .zone {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 28%;
    display: flex;
    align-items: center;
    pointer-events: none;
    animation: glow var(--flash) var(--ease-out) both;
  }
  .zone.back {
    left: 0;
    padding-left: calc(24px + var(--safe-left));
    background: linear-gradient(
      to right,
      rgba(77, 141, 255, 0.42),
      rgba(77, 141, 255, 0.12) 45%,
      transparent
    );
  }
  .zone.fwd {
    right: 0;
    justify-content: flex-end;
    padding-right: calc(24px + var(--safe-right));
    background: linear-gradient(to left, rgba(77, 141, 255, 0.42), rgba(77, 141, 255, 0.12) 45%, transparent);
  }
  .flash {
    width: 68px;
    height: 68px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(77, 141, 255, 0.4);
    color: #fff;
    box-shadow:
      inset 0 0 0 2px rgba(255, 255, 255, 0.55),
      0 0 50px 14px rgba(77, 141, 255, 0.45),
      0 8px 30px rgba(0, 0, 0, 0.35);
    animation: tap-pop var(--flash) var(--ease-pop) both;
  }
  @keyframes glow {
    0% {
      opacity: 0;
    }
    18% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
  @keyframes tap-pop {
    0% {
      transform: scale(0.7);
    }
    30% {
      transform: scale(1.12);
    }
    100% {
      transform: scale(1);
    }
  }
  .pages-num {
    min-width: 64px;
  }
  .end-next {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 14px 20px;
    border-radius: var(--r-lg);
    background: var(--accent);
    color: var(--on-accent);
    font-weight: 700;
  }
  .end-next .t3 {
    color: inherit;
    opacity: 0.75;
  }
  @media (max-width: 700px) {
    .lbl {
      display: none;
    }
  }
</style>
